import { lowerFirst } from "lodash";
import { FieldComposer, fieldToConfig } from "@/features/form-fields";
import type { Field } from "@/entities/form-metadata";
import { NameAssertion } from "@/entities/form-metadata";
import type { GridSpan } from "@/shared/components/Grid";
import { Grid } from "@/shared/components/Grid";

interface SelfNameFieldProps {
    field: Field<NameAssertion>;
    spanDesktop: GridSpan;
    onFocus?: () => void;
}

/**
 * Renders one field (GivenName/Surname/SecondSurname) of the primary Self name group — the
 * search subject's own name. Reads and writes the fixed `name.*` dataModel slot and uses the
 * compound name-exactness control (phonetic/similar/initials/soundex flags), matching legacy's
 * dedicated name-search behavior. See `NonSelfNameField` for a named non-Self party (e.g. an
 * Executor), which has neither of those.
 */
export const SelfNameField = ({ field, spanDesktop, onFocus }: SelfNameFieldProps) => {
    const {
        assertion,
        features: { gridColumnSpan },
    } = field;

    return (
        <Grid.Field
            key={assertion}
            spanDesktop={(gridColumnSpan as GridSpan) ?? spanDesktop}
            spanTablet={(gridColumnSpan as GridSpan) ?? spanDesktop}
            spanMobile={12}
        >
            <FieldComposer
                fieldPath={`name.${lowerFirst(assertion)}`}
                {...(assertion !== NameAssertion.SecondSurname
                    ? { exactnessFieldPath: `name.${lowerFirst(assertion)}Exactness` }
                    : {})}
                fieldConfig={fieldToConfig(field)}
            >
                <FieldComposer.Label />
                <FieldComposer.FieldConnector>
                    {({ formField, fieldPath, fieldConfig }, fieldHtmlId) => (
                        <FieldComposer.Inputs.String
                            id={fieldHtmlId}
                            data-testid={fieldPath}
                            required={fieldConfig.required}
                            minLength={fieldConfig.minLength}
                            {...formField}
                            onFocus={onFocus}
                        />
                    )}
                </FieldComposer.FieldConnector>
                {assertion !== NameAssertion.SecondSurname && <FieldComposer.NameExactness />}
            </FieldComposer>
        </Grid.Field>
    );
};
